/**
 * Case Studies
 * Vue app for /printing
 */

 /**
  * @section Template
  */

<template>
<div class="container">
  <div class="g g--i g--vc">
    <div class="g-b g-b--2of4_m pt1 pb1">
      <div class="print-heading">
        <h1>Printing</h1>
        <div v-html="printingCopy"></div>
      </div>
    </div>
    <div class="g-b g-b--2of4_m">
      <div class="budget">
        <h2 class="budget-title mb1">Get a Quote</h2>
        <div class="g-b tac mt025">
          <a :href="`/quote`" class="btn btn--prime">Request a quote</a>
        </div>
      </div>
    </div>
  </div>

  <div class="printPiece-nav">
    <div class="g g--vc bb btl">
      <div class="g-b g-b--1of4_m g-b--first_m">
        <div class="printPiece-count">
          <span class="printPiece-count-num">{{ filteredPrintPieces.length }}</span>
          match<span v-show="filteredPrintPieces.length !== 1">es</span>
        </div>
      </div>
      <div class="g-b g-b--2of4 g-b--1of4_m">
        <div class="dropdown" :class="{ 'is-open': isDropdownOpen }">
          <button class="dropdown-trigger" @click="toggleDropdown">
            <div class="dropdown-label">Products</div>
            <div class="dropdown-preview">{{ selectedProducts | toString('All Products') }}</div>
          </button>
          <menu class="dropdown-menu">
            <li
              v-for="printType in printTypes"
              :key="printType.id"
              v-if="isProduct(printType)"
              class="dropdown-menu-item"
            >
              <input
                class="form-check form-check--s"
                type="checkbox"
                name="print_type_ids[]"
                number
                v-model="selectedPrintTypes"
                :id="`type_${printType.id}`"
                :value="printType.id"
              >
              <label :for="`type_${printType.id}`">{{ printType.name }}</label>
            </li>
          </menu>
        </div>
        <div class="dropdown-overlay" @click="closeDropdown"></div>
      </div>
      <div class="g-b g-b--2of4 g-b--1of4_m">
        <div class="dropdown" :class="{ 'is-open': isDropdownOpen }">
          <button class="dropdown-trigger" @click="toggleDropdown">
            <div class="dropdown-label">Processes</div>
            <div class="dropdown-preview">{{ selectedProcesses | toString('All Processes') }}</div>
          </button>
          <menu class="dropdown-menu">
            <li v-for="printType in printTypes" :key="printType.id" class="dropdown-menu-item" v-if="isProcess(printType)">
              <input
                class="form-check form-check--s"
                type="checkbox"
                name="print_type_ids[]"
                number
                v-model="selectedPrintTypes"
                :id="`type_${printType.id}`"
                :value="printType.id"
              >
              <label :for="`type_${printType.id}`">{{ printType.name }}</label>
            </li>
          </menu>
        </div>
        <div class="dropdown-overlay" @click="closeDropdown"></div>
      </div>
      <div class="g-b g-b--1of4_m">
        <div class="form-search" :class="{ 'is-searching': query.length > 0 }">
          <label class="form-search-label" for="s">Search</label>
          <input id="s" class="form-search-text" type="text" v-model="query" debounce="100">
          <button class="form-search-clear" @click="query = ''">✕</button>
        </div>
      </div>
    </div>
  </div>

  <div class="has-printPiece">
    <div class="printPiece-partial" v-if="isPartial">
      <strong>0</strong> exact matches. Showing only partial matches.
    </div>

    <a
      v-for="printPiece in concatenatedPrintPieces"
      :key="printPiece.id"
      class="printPiece"
      :href="`/printing/${printPiece.slug}`">
      <div class="printPiece-pic" :style="{ 'background-image': `url(${printPiece.hero.small.url})` }"></div>
        <div class="printPiece-info">
        <time class="printPiece-date">{{ printPiece.published_at | mYYYY }}</time>
        <h6 class="printPiece-title">{{ printPiece.title }}</h6>
        <table class="data data--v data--l">
          <tbody class="data-body">
            <tr class="data-row">
              <td class="data-col data-col--heading" style="width:38%;">Approx. Print Budget</td>
              <td class="data-col">
                <div v-if="printPiece.budget > 0">
                  {{ lowPrice(printPiece.budget) | price }} – {{ highPrice(printPiece.budget) | price }} for {{ printPiece.print_run | commas }}
                </div>
                <div v-else>
                  Not Listed
                </div>
              </td>
            </tr>
            <tr class="data-row">
              <td class="data-col data-col--heading">Product</td>
              <td class="data-col">
                <span
                  v-for="printType in printPiece.print_types"
                  :key="printType.id"
                  :class="{ 'tca': selectedPrintTypes.indexOf(printType.id) >= 0 }"
                  class="printPiece-type"
                  v-if="isProduct(printType)"
                  >
                  {{ printType.name }}
                </span>
              </td>
            </tr>
            <tr class="data-row">
              <td class="data-col data-col--heading">Processes</td>
              <td class="data-col">
                <span
                  v-if="isProcess(printType)"
                  v-for="printType in printPiece.print_types"
                  :key="printType.id"
                  :class="{ 'tca': selectedPrintTypes.indexOf(printType.id) >= 0 }"
                  class="printPiece-type"
                  >{{ printType.name }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </a>
  </div>
  <div class="printPiece-loadMore" v-if="showing < filteredPrintPieces.length">
    <button class="btn btn--prime printPiece-loadMore-btn" @click="loadMore">Load More</button>
  </div>
  <div class="printPiece-zeroState" v-if="!filteredPrintPieces.length">
    <div class="ttu tw7 ts16 mb05">0 Print Pieces Found</div>
    Please reduce the number of filters to get more matches
  </div>
</div>
</template>

/**
 * @section Logic
 */

<script>
import axios from 'axios';
import compact from 'lodash/compact';
import concat from 'lodash/concat';
import difference from 'lodash/difference';
import max from 'lodash/max';
import min from 'lodash/min';
import QueryString from 'query-string';

module.exports = {
  data() {
    return {
      defaultPageSize: 20,
      isDropdownOpen: false,
      isEmpty: true,
      isPartial: false,
      printingCopy: '',
      printPieces: [],
      printTypes: [],
      query: '',
      selectedPrintTypes: [],
      showing: 20,
    };
  },
  props: {
    token: String,
  },
  watch: {
    selectedPrintTypes() {
      this.updateURL();
    },
    query() {
      this.updateURL();
    },
  },
  computed: {
    filteredPrintPieces() {
      if (this.selectedPrintTypes.length === 0) return this.searchedPrintPieces;

      const exact = this.searchedPrintPieces.filter(printPiece =>
        this.matchExact(this.selectedPrintTypes, printPiece.printTypeIDs));

      this.isPartial = exact.length === 0;
      if (exact.length) return exact;

      const partial = this.searchedPrintPieces.filter(printPiece =>
        this.matchPartial(this.selectedPrintTypes, printPiece.printTypeIDs));

      this.isPartial = partial.length !== 0;
      return partial;
    },

    concatenatedPrintPieces() {
      return this.filteredPrintPieces.slice(0, this.showing);
    },

    searchedPrintPieces() {
      if (this.query.length === 0) return this.printPieces;

      const query = this.query.toLowerCase();
      return this.printPieces.filter(({ designer, description, end_client, from_the_designer, keywords, location, paper, pantones, project, title, typefaces }) =>
        `${designer}${description}${end_client}${from_the_designer}${keywords}${location}${paper}${pantones}${project}${title}${typefaces}`.toLowerCase().indexOf(query) !== -1
      );
    },

    selectedProcesses() {
      return this.printTypes.filter(({ id, product_or_process }) =>
        this.selectedPrintTypes.indexOf(id) !== -1 &&
        product_or_process.toLowerCase() === 'process');
    },

    selectedProducts() {
      return this.printTypes.filter(({ id, product_or_process }) =>
        this.selectedPrintTypes.indexOf(id) !== -1 &&
        product_or_process.toLowerCase() === 'product');
    },
  },

  filters: {
    toString(items, fallback) {
      if (items.length) return items.map(({ name }) => name).join(', ');
      return fallback;
    },
  },

  created() {
    this.printPieces = window.MSPrintPieces.map((printPiece) => {
      return Object.assign(
        printPiece,
        { printTypeIDs: printPiece.print_types.map(({ id }) => id) }
      );
    });

    this.printingCopy = window.MSPrintingCopy;
    this.printTypes = window.MSPrintTypes;

    const query = QueryString.parse(window.location.search, { arrayFormat: 'bracket' });
    if (query.printTypes) {
      this.selectedPrintTypes = typeof query.printTypes === 'string' ?
        [parseInt(query.printTypes, 10)] :
        query.printTypes.map(item => parseInt(item, 10));
    }
    if (query.showing) {
      this.showing = parseInt(query.showing);
    }
    if (query.q) {
      this.query = query.q;
    }
  },

  mounted() {
    document.addEventListener('keydown', (e) => {
      return e.keyCode === 27 ? this.closeDropdown() : true;
    });
  },

  methods: {
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    highPrice(number) {
      return number * window.env.printPieceBudgetTop;
    },
    lowPrice(number) {
      return number * window.env.printPieceBudgetBottom
    },
    matchExact(list1, list2) {
      return list1.filter(item => list2.indexOf(item) !== -1).length === list1.length;
    },
    matchPartial(list1, list2) {
      return list2.filter(item => list1.indexOf(item) !== -1).length !== 0;
    },
    isProcess(printType) {
      return printType.product_or_process.toLowerCase() === 'process';
    },
    isProduct(printType) {
      return printType.product_or_process.toLowerCase() === 'product';
    },
    loadMore() {
      const increase = this.showing += this.defaultPageSize;
      this.showing = increase;
      this.updateURL();
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    updateURL() {
      const state = {
        printTypes: this.selectedPrintTypes,
        showing: this.showing,
        q: this.query,
      };
      window.history.replaceState(
        state,
        this.selectedPrintTypes,
        `${window.location.pathname}?${QueryString.stringify(state, { arrayFormat: 'bracket' })}`,
      );
    },
  },
};
</script>



// WEBPACK FOOTER //
// components/CaseStudies.vue